import { useEffect, useMemo } from "react";
import "./WalletPreview.css";

/**
 * Maquette HTML/CSS du rendu attendu sur le téléphone (page « Test carte »).
 * Reproduit la mise en page imposée par chaque plateforme autour du visuel
 * généré par le backend (strip Apple / hero Google) : en-tête, champs,
 * code-barres. Les couleurs de texte suivent les mêmes règles que le backend
 * (_contrast_text / _blend d'apple_wallet.py).
 */

function hexToRgb(hex) {
  const h = (hex || "#cbb59a").replace("#", "");
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) || 0);
}

function contrastText(rgb) {
  const lum = 0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2];
  return lum > 150 ? [17, 17, 17] : [255, 255, 255];
}

function blend(a, b, t) {
  return a.map((v, i) => Math.round(v + (b[i] - v) * t));
}

function css(rgb) {
  return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
}

/** Pseudo-cursive Unicode (Mathematical Bold Script), comme le backend :
 * le programName Google n'accepte pas de police custom. */
function scriptText(text) {
  return [...(text || "").normalize("NFD")]
    .map((ch) => {
      const c = ch.codePointAt(0);
      if (c >= 65 && c <= 90) return String.fromCodePoint(0x1d4d0 + c - 65);
      if (c >= 97 && c <= 122) return String.fromCodePoint(0x1d4ea + c - 97);
      return ch;
    })
    .join("");
}
/** URL objet pour prévisualiser un fichier uploadé (révoquée au changement). */
function useObjectUrl(file) {
  const url = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  return url;
}

/** QR factice : grille déterministe + motifs de repérage, purement décoratif. */
function QrSvg() {
  const n = 21;
  const cells = [];
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const inFinder =
        (x < 7 && y < 7) || (x >= n - 7 && y < 7) || (x < 7 && y >= n - 7);
      if (!inFinder && (x * 31 + y * 17 + x * y * 7) % 5 < 2) cells.push([x, y]);
    }
  }
  const finder = (fx, fy) => (
    <g key={`${fx}-${fy}`}>
      <rect x={fx} y={fy} width="7" height="7" fill="#111" />
      <rect x={fx + 1} y={fy + 1} width="5" height="5" fill="#fff" />
      <rect x={fx + 2} y={fy + 2} width="3" height="3" fill="#111" />
    </g>
  );
  return (
    <svg className="wp-qr" viewBox={`0 0 ${n} ${n}`} role="img" aria-label="QR code factice">
      {cells.map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill="#111" />
      ))}
      {finder(0, 0)}
      {finder(n - 7, 0)}
      {finder(0, n - 7)}
    </svg>
  );
}

function Barcode({ altText = "TEST-XXXXXXXX" }) {
  return (
    <div className="wp-barcode wp-barcode-square">
      <QrSvg />
      <span className="wp-barcode-alt">{altText}</span>
    </div>
  );
}

/** Visuel photo+tampons (photo_stamps_strip côté backend) : nom du commerce
 * en cursive sur bandeau sombre + grille 2×5 — identique Apple et Google. */
function StampsPanel({ orgName, stamps }) {
  return (
    <>
      <div className="wp-photo-topbar wp-cursive">{orgName || "Test"}</div>
      <div className="wp-stamps-panel">
        {Array.from({ length: 10 }, (_, i) => (
          <span key={i} className={`wp-stamp ${i < stamps ? "wp-stamp-on" : ""}`}>
            {i < stamps ? "✓" : ""}
          </span>
        ))}
      </div>
    </>
  );
}

/** Aperçu Apple : mêmes éléments que la carte Google — pastille ronde +
 * nom du commerce (logo.png/logoText natifs), visuel identique au hero,
 * code-barres portant le numéro de série. Seul l'ordre diffère : iOS
 * impose le code-barres en bas de la carte. Pas de compteur sur le recto,
 * les tampons ne sont visibles que dans la grille du visuel ; la
 * récompense en jeu s'affiche entre le visuel et le QR (Apple seulement). */
function ApplePreview({ orgName, color, cardStyle, mode, stamps, points, niveau,
                        logoUrl, photoUrl, stripUrl, altText }) {
  const bg = hexToRgb(color);
  const fg = contrastText(bg);
  const label = css(blend(fg, bg, 0.3));
  const stripSrc = cardStyle === "photo" && mode !== "stamps" ? photoUrl : stripUrl;

  return (
    <div className="wp-apple" style={{ background: css(bg), color: css(fg) }}>
      {cardStyle === "fullbg" && (
        <div className="wp-apple-fullbg" style={{ backgroundImage: `url(${stripUrl})` }} />
      )}
      <div className="wp-apple-content">
        {/* En-tête natif logo.png + logoText : même tête que Google */}
        <div className="wp-apple-head">
          <span className="wp-google-logo">
            {logoUrl
              ? <img src={logoUrl} alt="Logo" />
              : <span className="wp-google-initial">{(orgName || "T").trim().charAt(0).toUpperCase()}</span>}
          </span>
          <span className="wp-google-issuer">{orgName || "Test"}</span>
          {mode === "points" && (
            // headerFields du pass : le compteur de points, en haut à droite
            <span className="wp-apple-headerfield">
              <span className="wp-afield-label">POINTS</span>
              <span className="wp-afield-value">{points}</span>
            </span>
          )}
        </div>

        {cardStyle === "photo" && mode === "stamps" ? (
          <StampsPanel orgName={orgName} stamps={stamps} />
        ) : cardStyle !== "fullbg" && (
          <div className={`wp-strip-wrap ${cardStyle === "photo" && stripSrc ? "wp-strip-photoshade" : ""}`}>
            {stripSrc
              ? <img className="wp-apple-strip" src={stripSrc} alt="Visuel de la carte" />
              : <div className="wp-apple-strip wp-strip-empty">Ajoute une photo pour l'aperçu</div>}
          </div>
        )}

        {niveau && (
          // secondaryFields du pass : la récompense en jeu
          <div className="wp-apple-fields">
            <div className="wp-afield">
              <span className="wp-afield-label" style={{ color: label }}>RÉCOMPENSE</span>
              <span className="wp-afield-value">{niveau}</span>
            </div>
          </div>
        )}

        <Barcode altText={altText} />
      </div>
    </div>
  );
}

function GooglePreview({ orgName, color, cardStyle, mode, stamps, points,
                         logoUrl, photoUrl, stripUrl, altText, testBanner }) {
  const bg = hexToRgb(color);
  const fg = contrastText(bg);
  // Même hero que la génération : photo brute seulement en photo+points,
  // sinon le visuel du backend (grille photo+tampons composée côté client)
  const heroSrc = cardStyle === "photo" && mode !== "stamps" ? photoUrl : stripUrl;

  return (
    <div className="wp-google" style={{ background: css(bg), color: css(fg) }}>
      {testBanner && <div className="wp-google-testbanner">TEST UNIQUEMENT</div>}
      <div className="wp-google-head">
        <span className="wp-google-logo">
          {logoUrl
            ? <img src={logoUrl} alt="Logo" />
            : <span className="wp-google-initial">{(orgName || "T").trim().charAt(0).toUpperCase()}</span>}
        </span>
        <span className="wp-google-issuer">{orgName || "Test"}</span>
      </div>
      <div className="wp-google-program">{scriptText(orgName || "Test")}</div>
      {/* Mode tampons : pas de compteur (loyaltyPoints non envoyé), les
          tampons ne sont visibles que dans la grille du hero. Mode points :
          compteur Points, comme l'objet Google réel. */}
      {mode === "points" && (
        <div className="wp-google-points">
          <span className="wp-google-pointslabel">Points</span>
          <span className="wp-google-pointsvalue">{points}</span>
        </div>
      )}
      <Barcode altText={altText} />
      {cardStyle === "photo" && mode === "stamps" ? (
        <div className="wp-google-heroblock">
          <StampsPanel orgName={orgName} stamps={stamps} />
        </div>
      ) : (
        <div className={`wp-google-heroblock wp-strip-wrap ${cardStyle === "photo" && heroSrc ? "wp-strip-photoshade" : ""}`}>
          {heroSrc
            ? <img className="wp-google-hero" src={heroSrc} alt="Bannière de la carte" />
            : <div className="wp-google-hero wp-strip-empty">Ajoute une photo pour l'aperçu</div>}
        </div>
      )}
    </div>
  );
}

function WalletPreview({ platform, orgName, color, cardStyle, mode, stamps,
                         points, niveau, logoFile, photoFile, stripUrl,
                         logoSrc, photoSrc, altText, testBanner = false }) {
  // Fichier en cours d'upload prioritaire, sinon l'image déjà enregistrée
  // côté serveur (logoSrc / photoSrc) — pour la page Personnalisation.
  const logoUrl = useObjectUrl(logoFile) || logoSrc || null;
  const photoUrl = useObjectUrl(photoFile) || photoSrc || null;
  const shared = { orgName, color, cardStyle, mode, stamps, points,
                   niveau, logoUrl, photoUrl, stripUrl, altText, testBanner };
  return (
    <div className="wp-wrap">
      <div className="wp-phone">
        {platform === "google"
          ? <GooglePreview {...shared} />
          : <ApplePreview {...shared} />}
      </div>
      <p className="wp-note">
        Maquette indicative (HTML/CSS) : la mise en page réelle est composée
        par {platform === "google" ? "Google Wallet" : "iOS"} — vérifie sur
        téléphone avant de valider.
      </p>
    </div>
  );
}

export default WalletPreview;
